import { SelectDropdown, SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import React, { useEffect, useMemo, useState } from 'react'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetAccountQuery, useUpdateAccountMutation } from '../../../store/accountUserSettings/accountUserSettings'
import { Col, Form, Row, Spinner, Stack } from 'react-bootstrap'
import { Button } from '../../../components/Button/Button'
import {
  AccountIntegrationConfigMappingType,
  CRMAccountIntegrationConfig,
  DescriptorType,
  DetailedCRMAccountIntegrationMapping,
  FieldDescriptor,
  getAccountCRMIntegrationConfig,
} from '../../../models/Account'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { IntegrationServiceId } from '../../../common/integrations'
import { useFilterIntegrationMappingTemplatesQuery } from '../../../store/integrations/integrations'
import { CRMUrlInputField } from './CRMUrlInputField'
import { useTracking } from '../../../apps/professional-app/fa/common/tracking/hooks'
import { TrackEvent } from '../../../apps/professional-app/fa/common/tracking'

export interface CRMIntegrationSettingsProps {
  serviceId: string
  onSaved: () => void
  onCancelled: () => void
}

export const defaultCrmMeetingExportObject: { [key: string]: SelectOption } = {
  wealthbox: { id: 'notes', label: 'Note' },
  salesforce: { id: 'Note', label: 'Note' },
  elements: { id: 'ContentNote', label: 'Note' },
  blackdiamond: { id: 'ContentNote', label: 'Note' },
  practifi: { id: 'ContentNote', label: 'Note' },
  xlr8: { id: 'XLR8CS__Critical_Note__c', label: 'Critical Note' },
  hubspot: { id: 'note', label: 'Note' },
  redtail: { id: 'notes', label: 'Note' },
  zoho: { id: 'Notes', label: 'Note' },
  dynamics: { id: 'annotations', label: 'Note' },
  engage: { id: 'annotations', label: 'Note' },
  smartoffice: { id: 'SmartPad', label: 'Note' },
  advisorengine: { id: 'workflows', label: 'Note' },
}

export const CRMIntegrationSettings: React.FC<CRMIntegrationSettingsProps> = ({ serviceId, onSaved, onCancelled }) => {
  const accountId = useAppSelector(selectAccountId)

  const { trackingClient } = useTracking()

  const { data: inboundMappingTemplatePaths, isFetching: isInboundMappingTemplatePathsFetching } = useFilterIntegrationMappingTemplatesQuery({
    active: true,
    sourcePlatform: serviceId,
    destinationObject: 'contact',
  })
  const { data: outboundMappingTemplatePaths, isFetching: isOutboundMappingTemplatePathsFetching } = useFilterIntegrationMappingTemplatesQuery({
    active: true,
    destinationPlatform: serviceId,
    sourceObject: 'meeting',
  })
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: 10 })
  const [triggerUpdateAccount, { isLoading: isAccountUpdating }] = useUpdateAccountMutation()

  const crmContactTypePreload: { [key: string]: SelectOption } = useMemo(() => {
    return {
      wealthbox: { id: 'contact', label: 'Contact' },
      salesforce: { id: 'contact', label: 'Contact' },
      elements: { id: 'contact', label: 'Contact' },
      blackdiamond: { id: 'contact', label: 'Contact' },
      practifi: { id: 'contact', label: 'Contact' },
      xlr8: { id: 'contact', label: 'Contact' },
      hubspot: { id: 'contact', label: 'Contact' },
      redtail: { id: 'contact', label: 'Contact' },
      zoho: { id: 'contact', label: 'Contact' },
      dynamics: { id: 'contact', label: 'Contact' },
      engage: { id: 'contact', label: 'Contact' },
      smartoffice: { id: 'contact', label: 'Contact' },
      advisorengine: { id: 'contact', label: 'Contact' },
    }
  }, [])

  const crmEnableCustomFieldsList: string[] = ['salesforce', 'practifi', 'xlr8']
  const crmEnableSaveToHousehold: string[] = ['elements', 'blackdiamond', 'engage']

  const [selectedMeetingCRMType, setSelectedMeetingCRMType] = useState<SelectOption>(defaultCrmMeetingExportObject[serviceId])
  const [selectedContactCRMType, setSelectedContactCRMType] = useState<SelectOption>(crmContactTypePreload[serviceId])
  const [contactCRMTypeOptions, setContactCRMTypeOptions] = useState<SelectOption[]>([])
  const [meetingCRMTypeOptions, setMeetingCRMTypeOptions] = useState<SelectOption[]>([])
  const [enableMeetingHouseholdExport, setEnableMeetingHouseholdExport] = useState<boolean>(false)
  const [crmEmailFieldName, setCrmEmailFieldName] = useState('')
  const [crmHouseholdFieldName, setCrmHouseholdFieldName] = useState('')
  const [loginUrl, setLoginUrl] = useState<string | null>(null)
  const [accountConfigIsCustom, setAccountConfigIsCustom] = useState<boolean>(false)
  useEffect(() => {
    if (account && account.data) {
      const serviceIntegration = getAccountCRMIntegrationConfig(account.data, serviceId)
      if (!serviceIntegration) {
        return
      }
      setLoginUrl(serviceIntegration.loginUrl)
      setAccountConfigIsCustom(serviceIntegration.isCustom ?? false)
      if (AccountIntegrationConfigMappingType.SESSION in serviceIntegration.mappings && outboundMappingTemplatePaths) {
        const accountConfigMappingSession =
          typeof serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION] === 'string'
            ? serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]
            : serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION].objectName
        const sessionMappingTemplateOptions = outboundMappingTemplatePaths.map((i) => ({
          id: i.destinationObject,
          label: i.name,
        }))
        setSelectedMeetingCRMType(
          sessionMappingTemplateOptions.find((option) => option.id === accountConfigMappingSession) ?? defaultCrmMeetingExportObject[serviceId],
        )
      }
      if (AccountIntegrationConfigMappingType.CONTACT in serviceIntegration.mappings && inboundMappingTemplatePaths) {
        const accountConfigMappingContact =
          typeof serviceIntegration.mappings[AccountIntegrationConfigMappingType.CONTACT] === 'string'
            ? serviceIntegration.mappings[AccountIntegrationConfigMappingType.CONTACT]
            : serviceIntegration.mappings[AccountIntegrationConfigMappingType.CONTACT].objectName
        const contactMappingTemplateOptions = inboundMappingTemplatePaths.map((i) => ({
          id: i.sourceObject,
          label: i.name,
        }))
        setSelectedContactCRMType(contactMappingTemplateOptions.find((option) => option.id === accountConfigMappingContact) ?? crmContactTypePreload[serviceId])
      }
      if (AccountIntegrationConfigMappingType.CONTACT in serviceIntegration.mappings) {
        const mapping = serviceIntegration.mappings[AccountIntegrationConfigMappingType.CONTACT]
        const crmEmailFieldName = typeof mapping === 'string' ? '' : (mapping.fields?.['email']?.[0]?.value ?? '')
        setCrmEmailFieldName(crmEmailFieldName)
      }
      if (AccountIntegrationConfigMappingType.CONTACT in serviceIntegration.mappings) {
        const mapping = serviceIntegration.mappings[AccountIntegrationConfigMappingType.CONTACT]
        const crmHouseholdFieldName = typeof mapping === 'string' ? '' : (mapping.fields?.['household']?.[0]?.value ?? '')
        setCrmHouseholdFieldName(crmHouseholdFieldName)
      }
      if (AccountIntegrationConfigMappingType.SESSION in serviceIntegration.mappings) {
        const mapping = serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]
        const crmMeetingHouseholdExport =
          typeof mapping === 'string'
            ? false
            : mapping.additionalInformation != null &&
                'saveToHousehold' in mapping.additionalInformation &&
                typeof mapping.additionalInformation.saveToHousehold === 'boolean'
              ? mapping.additionalInformation.saveToHousehold
              : false
        setEnableMeetingHouseholdExport(crmMeetingHouseholdExport)
      }
    }
  }, [account, crmContactTypePreload, serviceId, inboundMappingTemplatePaths, outboundMappingTemplatePaths])

  useEffect(() => {
    if (!inboundMappingTemplatePaths || isInboundMappingTemplatePathsFetching) {
      return
    }
    const contactMappingTemplateOptions = inboundMappingTemplatePaths.map((i) => ({
      id: i.sourceObject,
      label: i.name,
    }))
    if (contactMappingTemplateOptions) {
      setContactCRMTypeOptions(contactMappingTemplateOptions)
    }
  }, [inboundMappingTemplatePaths, serviceId, isInboundMappingTemplatePathsFetching])

  useEffect(() => {
    if (!outboundMappingTemplatePaths || isOutboundMappingTemplatePathsFetching) {
      return
    }
    const sessionMappingTemplateOptions = outboundMappingTemplatePaths.map((i) => ({
      id: i.destinationObject,
      label: i.name,
    }))
    if (sessionMappingTemplateOptions) {
      setMeetingCRMTypeOptions(sessionMappingTemplateOptions)
      setMeetingCRMTypeOptions(sessionMappingTemplateOptions)
    }
  }, [outboundMappingTemplatePaths, serviceId, isOutboundMappingTemplatePathsFetching])

  const handleClose = () => {
    onCancelled()
  }

  const handleSave = async () => {
    try {
      if (!account) {
        return
      }
      const otherIntegrations = account.data.effectiveConfig.integrations?.filter((config) => config.serviceId !== serviceId) ?? []
      const currentIntegration = getAccountCRMIntegrationConfig(account.data, serviceId)
      const serviceIntegrationMappings = currentIntegration?.mappings ?? {}
      const sessionMappingDisplayName =
        typeof currentIntegration?.mappings[AccountIntegrationConfigMappingType.SESSION] === 'string'
          ? null
          : currentIntegration?.mappings[AccountIntegrationConfigMappingType.SESSION].displayName
      const contactMappingDisplayName =
        typeof currentIntegration?.mappings[AccountIntegrationConfigMappingType.CONTACT] === 'string'
          ? null
          : currentIntegration?.mappings[AccountIntegrationConfigMappingType.CONTACT].displayName
      const contactMapping: DetailedCRMAccountIntegrationMapping = {
        objectName: selectedContactCRMType.id,
        displayName: contactMappingDisplayName,
      }

      const contactFields: { [key: string]: FieldDescriptor[] } = {}
      const contactIntegrationMapping = serviceIntegrationMappings[AccountIntegrationConfigMappingType.CONTACT]
      const emailField = contactIntegrationMapping instanceof Object && contactIntegrationMapping.fields ? contactIntegrationMapping.fields['email'] : []
      const currentContactObjectName = contactIntegrationMapping instanceof Object ? contactIntegrationMapping.objectName : contactIntegrationMapping
      if (selectedContactCRMType.id !== currentContactObjectName) {
        trackingClient.track({
          eventName: TrackEvent.INTEGRATION_CONFIG_CONTACT_OBJECT_MODIFIED,
          eventDetails: {
            serviceId,
            previousObjectName: currentContactObjectName,
            newObjectName: selectedContactCRMType.id,
          },
        })
      }

      if (crmEmailFieldName.trim() !== '') {
        if (!(emailField?.map((f) => f.value) ?? []).includes(crmEmailFieldName)) {
          trackingClient.track({
            eventName: TrackEvent.INTEGRATION_CONFIG_EMAIL_FIELD_FILLED,
            eventDetails: {
              serviceId,
            },
          })
        }
        contactFields['email'] = [{ type: DescriptorType.FIELD, value: crmEmailFieldName }]
      }

      const householdField =
        contactIntegrationMapping instanceof Object && contactIntegrationMapping.fields ? contactIntegrationMapping.fields['household'] : []

      if (crmHouseholdFieldName.trim() !== '') {
        if (!(householdField?.map((f) => f.value) ?? []).includes(crmHouseholdFieldName)) {
          trackingClient.track({
            eventName: TrackEvent.INTEGRATION_CONFIG_HOUSEHOLD_FIELD_FILLED,
            eventDetails: {
              serviceId,
            },
          })
        }
        contactFields['household'] = [{ type: DescriptorType.FIELD, value: crmHouseholdFieldName }]
      }
      if (Object.keys(contactFields).length > 0) {
        contactMapping.fields = contactFields
      }
      const sessionIntegrationMapping = serviceIntegrationMappings[AccountIntegrationConfigMappingType.SESSION]

      const sessionMapping: DetailedCRMAccountIntegrationMapping = {
        objectName: selectedMeetingCRMType.id,
        displayName: sessionMappingDisplayName,
      }

      const currentSessionObjectName = sessionIntegrationMapping instanceof Object ? sessionIntegrationMapping.objectName : sessionIntegrationMapping
      if (selectedMeetingCRMType.id !== currentSessionObjectName) {
        trackingClient.track({
          eventName: TrackEvent.INTEGRATION_CONFIG_SESSION_OBJECT_MODIFIED,
          eventDetails: {
            serviceId,
            previousObjectName: currentSessionObjectName,
            newObjectName: selectedMeetingCRMType.id,
          },
        })
      }

      if (enableMeetingHouseholdExport) {
        if (sessionIntegrationMapping instanceof Object && sessionIntegrationMapping.additionalInformation?.saveToHousehold !== true) {
          trackingClient.track({
            eventName: TrackEvent.INTEGRATION_CONFIG_SAVE_TO_HOUSEHOLD_TOGGLED,
            eventDetails: {
              serviceId,
            },
          })
        }
        sessionMapping.additionalInformation = { saveToHousehold: true }
      }
      const currentUpdatedIntegration: CRMAccountIntegrationConfig = {
        type: 'crm',
        serviceId,
        loginUrl,
        mappings: {
          ...serviceIntegrationMappings,
          [AccountIntegrationConfigMappingType.SESSION]: sessionMapping,
          [AccountIntegrationConfigMappingType.CONTACT]: contactMapping,
        },
        enableFullMeetingExport: currentIntegration?.enableFullMeetingExport ?? true,
        enableNoteExport: currentIntegration?.enableNoteExport ?? true,
      }
      await triggerUpdateAccount({
        accountId,
        payload: {
          config: {
            ...account.data.config,
            integrations: [...otherIntegrations, currentUpdatedIntegration],
          },
        },
      }).unwrap()
      onSaved()
    } catch (e) {
      console.error(e)
    }
  }

  if (isAccountFetching || isInboundMappingTemplatePathsFetching || isOutboundMappingTemplatePathsFetching) {
    return (
      <div className='d-flex align-items-center justify-content-center mb-4'>
        <Spinner />
      </div>
    )
  }

  return (
    <div className='d-flex flex-column gap-5'>
      <div className='d-flex flex-column gap-2'>
        <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'account:update', superScope: ['account:admin'] }}>
          {serviceId === IntegrationServiceId.SALESFORCE && (
            <CRMUrlInputField
              label='CRM Login url (Optional):'
              tooltipText='Add your custom salesforce login URL. Make sure it starts with https://'
              value={loginUrl}
              onChange={setLoginUrl}
              placeholder='https://login.salesforce.com/'
              helperText='Keep it blank to use the default login url.'
              disabled={accountConfigIsCustom}
            />
          )}
          {(serviceId === IntegrationServiceId.DYNAMICS || serviceId === IntegrationServiceId.ENGAGE) && (
            <CRMUrlInputField
              label='Dynamics custom URL (Optional):'
              tooltipText='Add your custom Dynamics URL. Make sure it starts with https://'
              value={loginUrl}
              onChange={setLoginUrl}
              placeholder='https://custom-domain.crm.dynamics.com'
              helperText='Keep it blank to use the default url.'
              disabled={accountConfigIsCustom}
            />
          )}
          <div>
            <label className='label-medium'>
              Export meeting saves to this <span className='text-capitalize'>{serviceId}</span> object:
            </label>
            <SelectDropdown
              className='mt-1'
              initialSelectedOption={selectedMeetingCRMType}
              options={meetingCRMTypeOptions}
              onOptionSelected={setSelectedMeetingCRMType}
              disabled={accountConfigIsCustom}
            />
          </div>
          {crmEnableCustomFieldsList.includes(serviceId) || crmEnableSaveToHousehold.includes(serviceId) ? (
            <div>
              <Row className='mb-1 mt-1'>
                <Col>
                  <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
                    <div>Export meeting to household</div>
                  </Stack>
                </Col>
                <Col md='auto'>
                  <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
                    <Form.Switch
                      id='export-meeting-household-switch'
                      label=''
                      checked={enableMeetingHouseholdExport}
                      onChange={(e) => setEnableMeetingHouseholdExport(e.currentTarget.checked)}
                      disabled={accountConfigIsCustom}
                    />
                  </Stack>
                </Col>
              </Row>
            </div>
          ) : null}
          <div>
            <label className='label-medium'>
              Contact object in your <span className='text-capitalize'>{serviceId}</span>:
            </label>
            <SelectDropdown
              className='mt-1'
              initialSelectedOption={selectedContactCRMType}
              options={contactCRMTypeOptions}
              onOptionSelected={setSelectedContactCRMType}
              disabled={accountConfigIsCustom}
            />
          </div>
          {crmEnableCustomFieldsList.includes(serviceId) ? (
            <div>
              <div>
                <label className='label-medium'>Email field</label>
                <Form.Control
                  className='mt-1'
                  value={crmEmailFieldName}
                  onChange={(e) => setCrmEmailFieldName(e.currentTarget.value)}
                  placeholder='Email field name'
                  disabled={accountConfigIsCustom}
                />
                <Form.Text className='text-muted'>Keep it blank to use the default "Email" field.</Form.Text>
              </div>
              <div>
                <label className='label-medium'>Household field</label>
                <Form.Control
                  className='mt-1'
                  value={crmHouseholdFieldName}
                  onChange={(e) => setCrmHouseholdFieldName(e.currentTarget.value)}
                  placeholder='Household field name'
                  disabled={accountConfigIsCustom}
                />
                <Form.Text className='text-muted'>Keep it blank to use the default "Household" field.</Form.Text>
              </div>
            </div>
          ) : null}
        </ScopedBlock>
      </div>
      <div className='d-flex gap-3 justify-content-end mb-4'>
        <Button variant='secondary' onClick={handleClose} disabled={isAccountUpdating}>
          Cancel
        </Button>
        <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'account:update', superScope: ['account:admin'] }}>
          <Button variant='primary' onClick={handleSave} loading={isAccountUpdating} disabled={accountConfigIsCustom}>
            Save
          </Button>
        </ScopedBlock>
      </div>
    </div>
  )
}
